/**
 * 종합 분석 엔진 — 5가지 검사(career, study_style, iq, english, literacy) 결과를
 * 융합하여 최종 진로·전공·직업 예측, 과목별 강점/약점 분석,
 * 극복 방안, 선행학습 권장 수준을 도출한다.
 */

/* ─────────── 매핑 테이블 ─────────── */

// Holland 유형 → 과목 강점 (가중치)
const HOLLAND_SUBJECT_MAP: Record<string, Record<string, number>> = {
  R: { 수학: 3, 과학: 3, 체육: 2, 기술: 2, 국어: 0, 영어: 0, 사회: 0 },
  I: { 수학: 3, 과학: 3, 국어: 1, 사회: 1, 영어: 1, 체육: 0, 기술: 1 },
  A: { 국어: 3, 사회: 2, 영어: 2, 체육: 1, 기술: 1, 수학: 0, 과학: 0 },
  S: { 사회: 3, 영어: 2, 국어: 2, 체육: 1, 수학: 0, 과학: 0, 기술: 0 },
  E: { 영어: 3, 사회: 3, 국어: 2, 수학: 1, 과학: 0, 체육: 1, 기술: 1 },
  C: { 수학: 3, 영어: 2, 과학: 1, 국어: 1, 사회: 1, 기술: 2, 체육: 0 },
};

// Holland 유형 → 계열
const HOLLAND_TRACK_MAP: Record<string, string[]> = {
  R: ['공학계열', '자연계열'],
  I: ['자연계열', '공학계열', '의학계열'],
  A: ['예체능계열', '인문계열', '사회계열'],
  S: ['사회계열', '인문계열', '교육계열'],
  E: ['사회계열', '경영계열', '인문계열'],
  C: ['자연계열', '공학계열', '경영계열'],
};

// VARK → 과목 적합성
const VARK_SUBJECT_MAP: Record<string, Record<string, number>> = {
  V: { 수학: 2, 과학: 3, 국어: 1, 사회: 2, 영어: 1, 기술: 2, 체육: 1 },
  A: { 영어: 3, 사회: 2, 국어: 2, 수학: 0, 과학: 1, 기술: 0, 체육: 1 },
  R: { 국어: 3, 사회: 3, 영어: 2, 수학: 1, 과학: 1, 기술: 1, 체육: 0 },
  K: { 과학: 3, 체육: 3, 수학: 2, 기술: 2, 국어: 0, 사회: 0, 영어: 0 },
};

// MBTI → 학습 환경/동기 → 과목 적합성 (간접 매핑)
const MBTI_SUBJECT_MAP: Record<string, Record<string, number>> = {
  INTJ: { 수학: 3, 과학: 3, 국어: 1, 기술: 2, 사회: 0, 영어: 1, 체육: 0 },
  INTP: { 수학: 3, 과학: 3, 기술: 2, 국어: 1, 사회: 0, 영어: 1, 체육: 0 },
  ENTJ: { 사회: 3, 영어: 3, 수학: 2, 과학: 1, 국어: 1, 기술: 1, 체육: 1 },
  ENTP: { 과학: 3, 기술: 3, 수학: 2, 영어: 2, 사회: 1, 국어: 0, 체육: 1 },
  INFJ: { 국어: 3, 사회: 3, 영어: 2, 수학: 0, 과학: 0, 기술: 1, 체육: 0 },
  INFP: { 국어: 3, 사회: 2, 영어: 2, 예체능: 2, 수학: 0, 과학: 0, 기술: 1 },
  ENFJ: { 사회: 3, 영어: 3, 국어: 2, 수학: 0, 과학: 0, 기술: 1, 체육: 1 },
  ENFP: { 국어: 3, 영어: 3, 예체능: 2, 사회: 2, 수학: 0, 과학: 1, 기술: 1 },
  ISTJ: { 수학: 3, 영어: 2, 과학: 2, 기술: 1, 국어: 1, 사회: 1, 체육: 0 },
  ISFJ: { 국어: 2, 사회: 2, 영어: 2, 수학: 1, 과학: 1, 기술: 1, 체육: 0 },
  ESTJ: { 사회: 3, 영어: 3, 수학: 2, 과학: 1, 국어: 1, 기술: 1, 체육: 1 },
  ESFJ: { 사회: 3, 영어: 3, 국어: 2, 수학: 0, 과학: 0, 기술: 1, 체육: 1 },
  ISTP: { 수학: 3, 과학: 3, 기술: 3, 체육: 2, 국어: 0, 사회: 0, 영어: 0 },
  ISFP: { 예체능: 3, 국어: 2, 사회: 2, 체육: 2, 수학: 0, 과학: 0, 기술: 1 },
  ESTP: { 체육: 3, 과학: 2, 기술: 2, 수학: 1, 사회: 1, 영어: 1, 국어: 0 },
  ESFP: { 체육: 3, 예체능: 3, 영어: 2, 사회: 2, 수학: 0, 과학: 0, 기술: 0 },
};

// MBTI → 대표 직업
const MBTI_JOB_MAP: Record<string, string[]> = {
  INTJ: ['전략 컨설턴트', '데이터 과학자', '시스템 설계자'],
  INTP: ['연구원', '개발자', '이론 물리학자'],
  ENTJ: ['경영자', '프로젝트 매니저', 'CEO'],
  ENTP: ['스타트업 창업자', '마케터', '변호사'],
  INFJ: ['심리상담사', '작가', '사회복지사'],
  INFP: ['예술가', '번역가', '환경운동가'],
  ENFJ: ['교사', 'HR 매니저', '정치인'],
  ENFP: ['기획자', '콘텐츠 크리에이터', '광고기획'],
  ISTJ: ['회계사', '공무원', '품질관리 전문가'],
  ISFJ: ['간호사', '사서', '유치원 교사'],
  ESTJ: ['관리자', '군인', '판사'],
  ESFJ: ['의료 코디네이터', '행사 기획자', '취업 컨설턴트'],
  ISTP: ['엔지니어', '파일럿', '스포츠 코치'],
  ISFP: ['패션 디자이너', '요리사', '사진작가'],
  ESTP: ['영업 전문가', '부동산 중개인', '스포츠 선수'],
  ESFP: ['연예인', '여행 가이드', '이벤트 플래너'],
};

// IQ 수준 → 과목 난이도 적합성
function getIqSubjectWeight(iq: number): Record<string, number> {
  if (iq >= 120) return { 수학: 3, 과학: 3, 기술: 2, 국어: 2, 영어: 2, 사회: 1, 체육: 1 };
  if (iq >= 110) return { 수학: 3, 과학: 2, 국어: 2, 영어: 2, 사회: 2, 기술: 1, 체육: 1 };
  if (iq >= 90) return { 국어: 3, 사회: 3, 영어: 2, 수학: 1, 과학: 1, 기술: 1, 체육: 2 };
  if (iq >= 80) return { 국어: 3, 사회: 3, 영어: 3, 체육: 2, 수학: 0, 과학: 0, 기술: 0 };
  return { 국어: 3, 사회: 3, 영어: 2, 체육: 2, 수학: 0, 과학: 0, 기술: 0 };
}

// 선행학습 권장 단계 라벨
export const LEVEL_LABELS: Record<number, string> = {
  1: '기초 복습',
  2: '현재 학년',
  3: '1년 선행',
  4: '2년 선행',
  5: '심화 탐구',
};

// 학업수행능력 수준 라벨 (국·영·수·사·과·예체능)
export const ACADEMIC_LEVEL_LABELS: Record<number, string> = {
  1: '기초 보강',
  2: '다소 부족',
  3: '보통',
  4: '우수',
  5: '매우 우수',
};

// 학업수행능력 분석 대상 과목
export const ACADEMIC_SUBJECTS = ['국어', '영어', '수학', '사회', '과학', '예체능'] as const;

// 원시 과목 키 → 6대 교과 통합 매핑 (기술→과학, 체육·예체능→예체능)
const ACADEMIC_KEY_MAP: Record<string, string> = {
  국어: '국어',
  영어: '영어',
  수학: '수학',
  사회: '사회',
  과학: '과학',
  기술: '과학',
  체육: '예체능',
  예체능: '예체능',
};

/* ─────────── 타입 정의 ─────────── */

export interface CompositeInput {
  career?: CareerResult;
  study_style?: StudyStyleResult;
  iq?: IqResult;
  english?: EnglishResult;
  literacy?: LiteracyResult;
}

interface CareerResult {
  hollandScores?: Record<string, number>;
  valueScores?: Record<string, number>;
  topHolland?: string[];
  topValues?: string[];
  recommendedTrackIds?: string[];
  recommendedMajors?: string[];
  allJobs?: string[];
}

interface StudyStyleResult {
  vark?: string;
  mbti?: string;
}

interface IqResult {
  scaled?: number;
  iqRange?: [number, number];
  percentile?: number;
  categoryScores?: Record<string, { correct: number; total: number }>;
}

interface EnglishResult {
  level?: string;
  totalCorrect?: number;
  totalQuestions?: number;
  totalPct?: number;
  percentile?: number;
  typeScores?: Record<string, { correct: number; total: number }>;
}

interface LiteracyResult {
  totalCorrect?: number;
  totalQuestions?: number;
  vocabPct?: number;
  readingPct?: number;
  totalPct?: number;
  vocabScores?: Record<string, { correct: number; total: number }>;
  readingScores?: Record<string, { correct: number; total: number }>;
}

export interface SubjectInfo {
  name: string;
  score: number;
  reason: string;
}

export interface AdvancementInfo {
  name: string;
  level: number;
  levelLabel: string;
  reason: string;
}

export interface AcademicSubject {
  name: string;
  score: number;
  level: number;
  levelLabel: string;
  reason: string;
}

export interface AcademicAbilitySummary {
  subjects: AcademicSubject[];
  strongest: string;
  weakest: string;
  overallLevel: number;
  overallLabel: string;
  summary: string;
}

export interface CompositeAnalysis {
  hasAllResults: boolean;
  missingTests: string[];
  finalRecommendation: {
    careerTracks: string[];
    majors: string[];
    jobs: string[];
    description: string;
  };
  subjectAnalysis: {
    strengths: SubjectInfo[];
    weaknesses: SubjectInfo[];
  };
  weaknessRemedy: {
    strategies: string[];
    studyMethod: string;
  };
  subjectAdvancement: AdvancementInfo[];
  academicAbility: AcademicAbilitySummary;
  summaryTags: string[];
}

/* ─────────── 분석 함수 ─────────── */

function extractTopHolland(input: CompositeInput): string[] {
  const r = input.career;
  if (r?.topHolland?.length) return r.topHolland;
  if (r?.hollandScores) {
    return Object.entries(r.hollandScores)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([k]) => k);
  }
  return [];
}

function extractVark(input: CompositeInput): string | null {
  return input.study_style?.vark || null;
}

function extractMbti(input: CompositeInput): string | null {
  return input.study_style?.mbti || null;
}

function extractIq(input: CompositeInput): number | null {
  const iq = input.iq;
  if (!iq) return null;
  if (iq.iqRange) return iq.iqRange[0]; // lower bound
  return null;
}

// 영어 단어 검사 백분위 → 어휘·언어 능력 가중치 (0~5)
// 백분위는 "나보다 높은 비율"이므로 낮을수록(상위) 뛰어나다.
function englishWeight(input: CompositeInput): number {
  const p = input.english?.percentile;
  if (p == null) return 0;
  if (p <= 5) return 5;
  if (p <= 20) return 4;
  if (p <= 50) return 3;
  if (p <= 80) return 2;
  return 1;
}

// 어휘·문해력 정답률(%) → 국어(언어) 능력 가중치 (0~5)
function literacyWeight(input: CompositeInput): number {
  const pct = input.literacy?.totalPct;
  if (pct == null) return 0;
  if (pct >= 85) return 5;
  if (pct >= 70) return 4;
  if (pct >= 55) return 3;
  if (pct >= 40) return 2;
  return 1;
}

function computeSubjectScores(input: CompositeInput): Record<string, number> {
  const scores: Record<string, number> = {
    국어: 0, 수학: 0, 영어: 0, 사회: 0, 과학: 0, 체육: 0, 기술: 0,
  };

  // Holland 기반
  const topHolland = extractTopHolland(input);
  topHolland.forEach((code, idx) => {
    const map = HOLLAND_SUBJECT_MAP[code];
    if (!map) return;
    const weight = idx === 0 ? 3 : 1.5;
    Object.entries(map).forEach(([subj, val]) => {
      if (scores[subj] !== undefined) scores[subj] += val * weight;
    });
  });

  // VARK 기반
  const vark = extractVark(input);
  if (vark && VARK_SUBJECT_MAP[vark]) {
    Object.entries(VARK_SUBJECT_MAP[vark]).forEach(([subj, val]) => {
      if (scores[subj] !== undefined) scores[subj] += val * 2;
    });
  }

  // MBTI 기반
  const mbti = extractMbti(input);
  if (mbti && MBTI_SUBJECT_MAP[mbti]) {
    Object.entries(MBTI_SUBJECT_MAP[mbti]).forEach(([subj, val]) => {
      if (scores[subj] !== undefined) scores[subj] += val * 1.5;
    });
  }

  // IQ 기반
  const iq = extractIq(input);
  if (iq) {
    const iqMap = getIqSubjectWeight(iq);
    Object.entries(iqMap).forEach(([subj, val]) => {
      if (scores[subj] !== undefined) scores[subj] += val * 2;
    });
  }

  return scores;
}

function computeCareerTracks(input: CompositeInput): string[] {
  const trackScores: Record<string, number> = {};

  const topHolland = extractTopHolland(input);
  topHolland.forEach((code) => {
    (HOLLAND_TRACK_MAP[code] || []).forEach((track) => {
      trackScores[track] = (trackScores[track] || 0) + 2;
    });
  });

  const careerTracks = input.career?.recommendedTrackIds || [];
  careerTracks.forEach((id) => {
    const name = trackIdToName(id);
    if (name) trackScores[name] = (trackScores[name] || 0) + 3;
  });

  return Object.entries(trackScores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([k]) => k);
}

function trackIdToName(id: string): string | null {
  const map: Record<string, string> = {
    engineering: '공학계열',
    science: '자연계열',
    arts: '예체능계열',
    humanities: '인문계열',
    social: '사회계열',
    education: '교육계열',
    business: '경영계열',
    medical: '의학계열',
  };
  return map[id] || null;
}

function computeJobs(input: CompositeInput): string[] {
  const jobSet = new Set<string>();

  // MBTI 기반
  const mbti = extractMbti(input);
  if (mbti && MBTI_JOB_MAP[mbti]) {
    MBTI_JOB_MAP[mbti].forEach((j) => jobSet.add(j));
  }

  // career의 allJobs
  (input.career?.allJobs || []).forEach((j) => jobSet.add(j));

  const jobs = Array.from(jobSet);
  return jobs.slice(0, 5);
}

function computeMajors(input: CompositeInput): string[] {
  const majors = input.career?.recommendedMajors || [];
  return majors.slice(0, 5);
}

function generateDescription(input: CompositeInput): string {
  const topHolland = extractTopHolland(input);
  const vark = extractVark(input);
  const mbti = extractMbti(input);
  const iq = extractIq(input);

  const parts: string[] = [];

  if (topHolland.length >= 1) {
    const labels: Record<string, string> = {
      R: '현실형', I: '탐구형', A: '예술형', S: '사회형', E: '진취형', C: '관습형',
    };
    parts.push(`Holland 흥미유형 ${topHolland.map((c) => labels[c] || c).join('·')}을(를) 보이며`);
  }

  if (vark) {
    const varkLabels: Record<string, string> = {
      V: '시각형', A: '청각형', R: '읽기형', K: '체험형',
    };
    parts.push(`${varkLabels[vark] || vark} 학습자로서`);
  }

  if (mbti) {
    parts.push(`${mbti} 성격유형의 특성을 가진`);
  }

  if (iq) {
    if (iq >= 120) parts.push('높은 인지능력을 바탕으로 복잡한 학문 탐구에 적합한');
    else if (iq >= 110) parts.push('우수한 인지능력으로 다양한 학문 분야에서 성과를 낼 수 있는');
    else if (iq >= 90) parts.push('균형 잡힌 인지능력으로 꾸준한 학습을 통해 성장 가능한');
    else parts.push('기초 학습 안정화를 통해 점진적으로 역량을 키워나갈');
  }

  const engP = input.english?.percentile;
  if (engP != null) {
    if (engP <= 10) parts.push('영어 어휘력이 상위권으로 뛰어난');
    else if (engP <= 30) parts.push('영어 어휘력이 우수한');
    else if (engP <= 70) parts.push('영어 어휘력이 보통 수준의');
    else parts.push('영어 기초 어휘 보강이 필요한');
  }

  parts.push('학생입니다.');

  if (topHolland.length >= 2) {
    const code = topHolland[0];
    const trackMap: Record<string, string> = {
      R: '공학·기술 분야', I: '과학·연구 분야', A: '예술·창작 분야',
      S: '교육·복지 분야', E: '경영·리더십 분야', C: '관리·전문직 분야',
    };
    parts.push(`종합적으로 ${trackMap[code] || '다양한 분야'}에서 잠재력을 보입니다.`);
  }

  return parts.join(' ');
}

function computeWeaknessRemedy(
  weaknesses: SubjectInfo[],
  input: CompositeInput,
): { strategies: string[]; studyMethod: string } {
  const strategies: string[] = [];
  const vark = extractVark(input);
  const mbti = extractMbti(input);

  const varkMethods: Record<string, string> = {
    V: '도표, 그래프, 영상 자료를 활용한 시각적 학습',
    A: '녹음·강의 청취, 토론을 활용한 청각적 학습',
    R: '필기, 요약노트, 교재 정독을 활용한 독해 중심 학습',
    K: '실험, 문제 풀이, 체험 활동을 활용한 실습 중심 학습',
  };

  const mbtiTips: Record<string, string> = {
    INTJ: '체계적인 학습 계획 수립과 독립적 탐구',
    INTP: '개념 중심의 깊이 있는 이해와 논리적 정리',
    ENTJ: '목표 설정과 경쟁적 학습 환경 활용',
    ENTP: '다양한 학습 자료 탐색과 토론식 학습',
    INFJ: '의미 중심 학습과 비전 설정으로 동기부여',
    INFP: '흥미 중심 학습과 창의적 표현 기회 제공',
    ENFJ: '그룹 스터디와 상호 피드백 활용',
    ENFP: '다양한 학습 방식 번갈아 가며 흥미 유지',
    ISTJ: '단계별 학습 로드맵과 반복 연습',
    ISFJ: '꾸준한 복습과 체계적인 노트 정리',
    ESTJ: '명확한 규칙과 구조화된 학습 환경',
    ESFJ: '학습 파트너와 함께하는 협력 학습',
    ISTP: '실제 문제 해결을 통한 실습 중심 학습',
    ISFP: '자율적 학습 환경과 창의적 접근',
    ESTP: '경쟁 요소와 실시간 피드백 활용',
    ESFP: '게이미피케이션과 재미 요소를 가미한 학습',
  };

  if (weaknesses.length > 0) {
    const weakNames = weaknesses.map((w) => w.name).join('·');
    strategies.push(`${weakNames} 과목의 기초 개념을 먼저 다지고, 매일 20~30분씩 꾸준히 투자하세요.`);
  }

  if (vark) {
    strategies.push(`약점 과목도 ${varkMethods[vark] || '다양한 방식으로'} 접근하면 효과적입니다.`);
  }

  if (mbti && mbtiTips[mbti]) {
    strategies.push(`${mbtiTips[mbti]}이 약점 과목 극복에 도움이 됩니다.`);
  }

  strategies.push('주 2회 이상 해당 과목 문제 풀이를 하고, 오답 노트를 만들어 반복 복습하세요.');
  strategies.push('학원 선생님이나 과외 선생님에게 기초 개념부터 짚어달라고 요청하세요.');

  const studyMethod = vark
    ? `본인의 ${varkMethods[vark] || '학습 스타일'}을 약점 과목에 적용하고, ${mbti && mbtiTips[mbti] ? mbtiTips[mbti] + ' 방식으로' : '체계적으로'} 접근하세요.`
    : '약점 과목은 기초부터 차근차근 다지며, 다양한 학습 방식을 시도해 보면서 본인에게 맞는 방법을 찾으세요.';

  return { strategies, studyMethod };
}

function computeAdvancement(input: CompositeInput): AdvancementInfo[] {
  const subjScores = computeSubjectScores(input);
  const iq = extractIq(input) || 100;
  const topHolland = extractTopHolland(input);

  const subjects = ['국어', '수학', '영어', '사회', '과학', '기술'];

  return subjects.map((name) => {
    const score = subjScores[name] || 0;
    let level = 2; // 기본: 현재 학년
    let reason = '';

    // 점수에 따른 기본 레벨
    if (score >= 12) level = 5;
    else if (score >= 9) level = 4;
    else if (score >= 6) level = 3;
    else if (score >= 3) level = 2;
    else level = 1;

    // IQ 보정
    if (iq >= 120 && level < 4) {
      level += 1;
      reason = '높은 인지능력과 강한 학습 적성을 고려해 한 단계 높은 수준을 권장합니다.';
    } else if (iq <= 85 && level > 2) {
      level -= 1;
      reason = '기초 학습 안정화가 우선이므로 한 단계 낮춰 권장합니다.';
    }

    // Holland 보정
    const primaryHolland = topHolland[0];
    const hMap = HOLLAND_SUBJECT_MAP[primaryHolland || ''];
    if (hMap && hMap[name] >= 3 && level < 5) {
      level += 1;
      reason = reason || `${name}에 대한 흥미와 적성이 매우 높아 선행 학습을 적극 권장합니다.`;
    }

    level = Math.max(1, Math.min(5, level));

    if (!reason) {
      if (level >= 5) reason = '뛰어난 적성과 흥미를 보이므로 심화 탐구 수준의 학습이 권장됩니다.';
      else if (level === 4) reason = '우수한 학습 능력을 고려해 2년 선행 학습이 권장됩니다.';
      else if (level === 3) reason = '균형 잡힌 역량으로 1년 선행 학습이 적절합니다.';
      else if (level === 2) reason = '현재 학년 내용을 충실히 다지는 것이 우선입니다.';
      else reason = '기초 개념 복습을 통해 학습 안정화가 필요합니다.';
    }

    return { name, level, levelLabel: LEVEL_LABELS[level], reason };
  });
}

function computeSummaryTags(input: CompositeInput): string[] {
  const tags: string[] = [];
  const topHolland = extractTopHolland(input);
  const vark = extractVark(input);
  const mbti = extractMbti(input);
  const iq = extractIq(input);

  if (topHolland[0]) {
    const map: Record<string, string> = {
      R: '실천력 강한 학생', I: '탐구력 강한 학생', A: '창의력 강한 학생',
      S: '공감력 강한 학생', E: '리더십 강한 학생', C: '꼼꼼한 학생',
    };
    tags.push(map[topHolland[0]] || '');
  }

  if (vark) {
    const map: Record<string, string> = {
      V: '시각 학습자', A: '청각 학습자', R: '독해 학습자', K: '체험 학습자',
    };
    tags.push(map[vark] || '');
  }

  if (mbti) {
    tags.push(`${mbti} 유형`);
  }

  if (iq) {
    if (iq >= 130) tags.push('상위 2% 인지능력');
    else if (iq >= 120) tags.push('우수한 인지능력');
    else if (iq >= 110) tags.push('평균 이상 인지능력');
  }

  const engP = input.english?.percentile;
  if (engP != null) {
    if (engP <= 10) tags.push('영어 어휘 상위권');
    else if (engP <= 30) tags.push('영어 어휘 우수');
  }

  return tags.filter(Boolean);
}

function buildAcademicReason(
  name: string,
  level: number,
  input: CompositeInput,
): string {
  const iq = extractIq(input);

  if (level >= 4) {
    let base = `${name} 과목은 흥미·적성·학습 스타일이 잘 맞아 학업 수행능력이 높게 나타납니다.`;
    if (iq && iq >= 120) base += ' 높은 인지능력이 더해져 심화 학습도 수월할 것으로 예상됩니다.';
    return base;
  }

  if (level === 3) {
    return `${name} 과목은 보통 수준의 학업 수행능력을 보이며, 꾸준한 학습 습관을 통해 성적 향상이 기대됩니다.`;
  }

  let base = `${name} 과목은 현재 흥미나 학습 스타일과의 적합도가 낮아 기초 보강이 우선적으로 필요합니다.`;
  if (iq && iq <= 85) base += ' 인지능력 측면에서도 기초 학습 안정화가 권장됩니다.';
  return base;
}

function buildAcademicSummary(
  strongest: string,
  weakest: string,
  overallLevel: number,
): string {
  const levelDesc: Record<number, string> = {
    1: '전반적으로 기초 학습 안정화가 필요한 상태',
    2: '일부 과목에서 보완 학습이 필요한 상태',
    3: '전반적으로 보통 수준의 학업 수행능력을 보이는 상태',
    4: '전반적으로 우수한 학업 수행능력을 보이는 상태',
    5: '전반적으로 매우 우수한 학업 수행능력을 보이는 상태',
  };

  return `${levelDesc[overallLevel]}입니다. 가장 강한 과목은 '${strongest}', 상대적으로 보완이 필요한 과목은 '${weakest}'로 분석됩니다.`;
}

function computeAcademicAbility(input: CompositeInput): AcademicAbilitySummary {
  const raw: Record<string, number> = {
    국어: 0, 수학: 0, 영어: 0, 사회: 0, 과학: 0, 체육: 0, 기술: 0, 예체능: 0,
  };

  const addMap = (map: Record<string, number> | undefined, weight: number) => {
    if (!map) return;
    Object.entries(map).forEach(([key, val]) => {
      if (raw[key] !== undefined) raw[key] += val * weight;
    });
  };

  // Holland 흥미 기반 (1순위 ×3, 2순위 ×1.5)
  const topHolland = extractTopHolland(input);
  topHolland.forEach((code, idx) => {
    addMap(HOLLAND_SUBJECT_MAP[code], idx === 0 ? 3 : 1.5);
  });

  // VARK 학습 스타일 기반 (×2)
  const vark = extractVark(input);
  if (vark) addMap(VARK_SUBJECT_MAP[vark], 2);

  // MBTI 성격 기반 (×1.5)
  const mbti = extractMbti(input);
  if (mbti) addMap(MBTI_SUBJECT_MAP[mbti], 1.5);

  // IQ 인지능력 기반 (×2)
  const iq = extractIq(input);
  if (iq) addMap(getIqSubjectWeight(iq), 2);

  // 영어 단어 검사 기반 (언어 능력 반영: 영어 중심 + 국어 보조)
  const engW = englishWeight(input);
  if (engW > 0) {
    raw.영어 += engW * 2.5;
    raw.국어 += engW * 1;
  }

  // 어휘·문해력 검사 기반 (국어 언어 능력 반영)
  const litW = literacyWeight(input);
  if (litW > 0) {
    raw.국어 += litW * 2.5;
  }

  // 6대 교과로 통합
  const scores: Record<string, number> = {};
  ACADEMIC_SUBJECTS.forEach((s) => {
    scores[s] = 0;
  });
  Object.entries(raw).forEach(([key, val]) => {
    const target = ACADEMIC_KEY_MAP[key];
    if (target && scores[target] !== undefined) scores[target] += val;
  });

  const iqVal = iq || 100;

  const subjects: AcademicSubject[] = ACADEMIC_SUBJECTS.map((name) => {
    const score = scores[name] || 0;

    let level = 2;
    if (score >= 12) level = 5;
    else if (score >= 9) level = 4;
    else if (score >= 6) level = 3;
    else if (score >= 3) level = 2;
    else level = 1;

    // IQ 보정
    if (iqVal >= 120 && level < 5) level += 1;
    else if (iqVal <= 85 && level > 1) level -= 1;

    level = Math.max(1, Math.min(5, level));

    return {
      name,
      score,
      level,
      levelLabel: ACADEMIC_LEVEL_LABELS[level],
      reason: buildAcademicReason(name, level, input),
    };
  });

  const sortedAsc = [...subjects].sort((a, b) => a.level - b.level || a.score - b.score);
  const sortedDesc = [...subjects].sort((a, b) => b.level - a.level || b.score - a.score);

  const strongest = sortedDesc[0]?.name || '국어';
  const weakest = sortedAsc[0]?.name || '국어';

  const avgLevel = subjects.reduce((sum, s) => sum + s.level, 0) / subjects.length;
  const overallLevel = Math.max(1, Math.min(5, Math.round(avgLevel)));

  return {
    subjects,
    strongest,
    weakest,
    overallLevel,
    overallLabel: ACADEMIC_LEVEL_LABELS[overallLevel],
    summary: buildAcademicSummary(strongest, weakest, overallLevel),
  };
}

/* ─────────── 메인 분석 함수 ─────────── */

export function analyzeComposite(input: CompositeInput): CompositeAnalysis {
  const hasCareer = !!input.career;
  const hasStudyStyle = !!input.study_style;
  const hasIq = !!input.iq;
  const hasEnglish = !!input.english;
  const hasLiteracy = !!input.literacy;

  const missingTests: string[] = [];
  if (!hasCareer) missingTests.push('진로·계열 탐색');
  if (!hasStudyStyle) missingTests.push('공부법 검사');
  if (!hasIq) missingTests.push('IQ 테스트');
  if (!hasEnglish) missingTests.push('영어 단어 검사');
  if (!hasLiteracy) missingTests.push('어휘·문해력 검사');

  // 모든 검사(5종)를 완료해야만 종합 분석을 제공한다.
  const hasAllResults = hasCareer && hasStudyStyle && hasIq && hasEnglish && hasLiteracy;

  const subjScores = computeSubjectScores(input);

  // 강점 과목 (상위 3)
  const strengths = Object.entries(subjScores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([name, score]) => ({
      name,
      score,
      reason: `${name}에 대한 흥미·적성·학습 스타일이 모두 긍정적으로 나타납니다.`,
    }));

  // 약점 과목 (하위 2)
  const weaknesses = Object.entries(subjScores)
    .sort((a, b) => a[1] - b[1])
    .slice(0, 2)
    .map(([name, score]) => ({
      name,
      score,
      reason: `${name}은(는) 현재 흥미나 학습 스타일과 맞지 않아 추가적인 보완 학습이 필요합니다.`,
    }));

  const careerTracks = computeCareerTracks(input);
  const jobs = computeJobs(input);
  const majors = computeMajors(input);
  const description = generateDescription(input);
  const weaknessRemedy = computeWeaknessRemedy(weaknesses, input);
  const advancement = computeAdvancement(input);
  const academicAbility = computeAcademicAbility(input);
  const tags = computeSummaryTags(input);

  return {
    hasAllResults,
    missingTests,
    finalRecommendation: {
      careerTracks,
      majors,
      jobs,
      description,
    },
    subjectAnalysis: { strengths, weaknesses },
    weaknessRemedy,
    subjectAdvancement: advancement,
    academicAbility,
    summaryTags: tags,
  };
}

/* ─────────── 학습역량 육각형 레이더 차트 ─────────── */

export interface RadarAxisPoint {
  label: string;
  level: number;
  angleDeg: number;
  dataX: number;
  dataY: number;
  axisX: number;
  axisY: number;
  labelX: number;
  labelY: number;
  labelAnchor: 'start' | 'middle' | 'end';
}

export interface LearningRadarChart {
  centerX: number;
  centerY: number;
  viewWidth: number;
  viewHeight: number;
  axes: RadarAxisPoint[];
  grids: string[];
  dataPolygon: string;
}

const RADAR_CENTER_X = 160;
const RADAR_CENTER_Y = 150;
const RADAR_MAX_R = 95;
const RADAR_VIEW_WIDTH = 320;
const RADAR_VIEW_HEIGHT = 300;

function radarPoint(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: RADAR_CENTER_X + radius * Math.cos(rad),
    y: RADAR_CENTER_Y + radius * Math.sin(rad),
  };
}

export function buildLearningRadarChart(subjects: { name: string; level: number }[]): LearningRadarChart {
  const n = subjects.length;

  const axes: RadarAxisPoint[] = subjects.map((s, i) => {
    const angleDeg = -90 + i * (360 / n);
    const data = radarPoint(angleDeg, (s.level / 5) * RADAR_MAX_R);
    const axis = radarPoint(angleDeg, RADAR_MAX_R);
    const label = radarPoint(angleDeg, RADAR_MAX_R + 26);
    const labelAnchor =
      Math.abs(label.x - RADAR_CENTER_X) < 4 ? 'middle' : label.x < RADAR_CENTER_X ? 'end' : 'start';
    return {
      label: s.name,
      level: s.level,
      angleDeg,
      dataX: data.x,
      dataY: data.y,
      axisX: axis.x,
      axisY: axis.y,
      labelX: label.x,
      labelY: label.y,
      labelAnchor,
    };
  });

  const grids: string[] = [];
  for (let level = 1; level <= 5; level++) {
    const r = (level / 5) * RADAR_MAX_R;
    const pts: string[] = [];
    for (let i = 0; i < n; i++) {
      const p = radarPoint(-90 + i * (360 / n), r);
      pts.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
    }
    grids.push(pts.join(' '));
  }

  const dataPolygon = axes.map((a) => `${a.dataX.toFixed(1)},${a.dataY.toFixed(1)}`).join(' ');

  return {
    centerX: RADAR_CENTER_X,
    centerY: RADAR_CENTER_Y,
    viewWidth: RADAR_VIEW_WIDTH,
    viewHeight: RADAR_VIEW_HEIGHT,
    axes,
    grids,
    dataPolygon,
  };
}